<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import UdlGuidelineStrategyPanel from './UdlGuidelineStrategyPanel.vue'
import UdlBrainNetworkInfographic from './UdlBrainNetworkInfographic.vue'
import OpensInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import { getStrategiesGrouped } from '@/strategies/api'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { goalbookUrls } from '@/util/links'
import type { GroupedStrategiesPayload, UdlGuideline, UdlPrincipleNumeral } from './udl_index_types'

const ALL_GUIDELINES = 'all'
const GUIDELINE_LABEL_BOILERPLATE = /(Provide|Design) options for /g

const PRINCIPLE_NAMES: Record<UdlPrincipleNumeral, string> = {
  I: 'Representation',
  II: 'Action & Expression',
  III: 'Engagement'
}

const PRINCIPLE_COLUMN_ORDER: UdlPrincipleNumeral[] = ['III', 'I', 'II']

const PRINCIPLE_HEADER_CLASSES: Record<UdlPrincipleNumeral, string> = {
  I: 'bg-udl-representationLight',
  II: 'bg-udl-expressionLight',
  III: 'bg-udl-engagementLight'
}

const analytics = getAnalyticsTracker()
const route = useRoute()

const groupedStrategies = ref<GroupedStrategiesPayload | null>(null)
const fetching = ref(true)
const errorMessage = ref('')
const collapsedGuidelines = ref<Record<string, boolean>>({})
const collapsedCheckpoints = ref<Record<string, boolean>>({})

const principleColumns = computed(() =>
  PRINCIPLE_COLUMN_ORDER.map((numeral) => ({
    numeral,
    name: PRINCIPLE_NAMES[numeral],
    headerClass: PRINCIPLE_HEADER_CLASSES[numeral],
    guidelines: guidelinesForPrinciple(numeral)
  }))
)

function guidelinesForPrinciple(numeral: UdlPrincipleNumeral): UdlGuideline[] {
  const payload = groupedStrategies.value
  if (!payload) {
    return []
  }
  return Object.keys(payload.adaptations[numeral] || {})
    .sort()
    .map((key) => ({
      label: payload.guideline_mappings[key].replace(GUIDELINE_LABEL_BOILERPLATE, ''),
      checkpoints: payload.adaptations[numeral][key]
    }))
}

function isGuidelineExpanded(label: string) {
  return collapsedGuidelines.value[label] !== true
}

function expandedCheckpointsFor(guideline: UdlGuideline) {
  return Object.fromEntries(
    guideline.checkpoints.map((checkpoint) => [
      checkpoint.standard_long_ref,
      collapsedCheckpoints.value[checkpoint.standard_long_ref] !== true
    ])
  )
}

function setGuidelineExpanded(label: string, expanded: boolean) {
  collapsedGuidelines.value[label] = !expanded
}

function setCheckpointExpanded(checkpointRef: string, expanded: boolean) {
  collapsedCheckpoints.value[checkpointRef] = !expanded
}

function toggleAll(expanded: boolean) {
  analytics.sendEvent(EVENT_NAMES.UDL_INDEX.TOGGLED_GUIDELINE, {
    udl_guideline: ALL_GUIDELINES,
    udl_expanded: expanded
  })
  const labels = principleColumns.value.flatMap((column) =>
    column.guidelines.map((guideline) => guideline.label)
  )
  collapsedGuidelines.value = Object.fromEntries(labels.map((label) => [label, !expanded]))
}

async function loadStrategies() {
  try {
    groupedStrategies.value = await getStrategiesGrouped()
  } catch {
    errorMessage.value = 'Error retrieving UDL strategies. Please reload the page to try again.'
  } finally {
    fetching.value = false
  }
}

loadStrategies()

analytics.sendEvent(EVENT_NAMES.UDL_INDEX.VIEWED_PAGE, { ref: route.query.ref })
</script>

<template>
  <div class="container min-h-screen">
    <header>
      <h1 class="header-xl mt-8 text-center font-regular text-gray-900">UDL-Aligned Strategies</h1>
      <h2 class="mt-6 text-display-sm font-medium text-gray-900">What are UDL-aligned strategies?</h2>
      <div class="grid gap-6 md:grid-cols-3">
        <div class="text-text-xl text-gray-800 md:col-span-2">
          UDL-aligned strategies are instructional methods and tools used by teachers to ensure that ALL students
          have an equal opportunity to learn. All of our strategies are aligned with
          <a
            class="font-medium text-blue-400 hover:underline focus:outline-offset-4"
            :href="goalbookUrls.aboutUDL"
            target="_blank"
            rel="noopener"
            >Universal Design for Learning (UDL) <OpensInNewTabIcon
          /></a>
          guidelines. These guidelines help you to select strategies that remove barriers in instruction so that all
          students can achieve their learning goals.
        </div>
        <div class="text-center">
          <a
            :href="goalbookUrls.udlGuidelines"
            target="_blank"
            rel="noopener"
            class="inline-block text-blue-400 hover:underline focus:outline-offset-4"
          >
            <UdlBrainNetworkInfographic class="mb-6" />
            Learn more about UDL <OpensInNewTabIcon />
          </a>
        </div>
      </div>
    </header>

    <div>
      <p v-if="fetching" role="status" class="text-center text-text-xl text-gray-700">Loading strategies...</p>

      <p v-else-if="errorMessage" role="alert" class="text-center text-text-xl text-gray-900">{{ errorMessage }}</p>

      <template v-else>
        <div class="mb-0 mt-6 text-right text-text-sm text-gray-700">
          <button
            type="button"
            data-testid="collapse-all"
            class="text-blue-400 underline-offset-4 hover:underline focus:outline-offset-4"
            @click="toggleAll(false)"
          >
            Collapse all
          </button>
          |
          <button
            type="button"
            data-testid="expand-all"
            class="text-blue-400 underline-offset-4 hover:underline focus:outline-offset-4"
            @click="toggleAll(true)"
          >
            Expand all
          </button>
        </div>
        <div class="grid gap-x-8 md:grid-cols-3">
          <div v-for="column in principleColumns" :key="column.numeral" data-testid="principle-column">
            <h3 class="rounded-xs px-3 py-1.5 text-display-sm font-[200] leading-[1.61em] text-gray-900" :class="column.headerClass">
              Strategies for {{ column.name }}
            </h3>
            <ul class="list-none">
              <li v-for="guideline in column.guidelines" :key="guideline.label">
                <UdlGuidelineStrategyPanel
                  :guideline="guideline"
                  :principle="column.name"
                  :expanded="isGuidelineExpanded(guideline.label)"
                  :expanded-checkpoints="expandedCheckpointsFor(guideline)"
                  @update:expanded="setGuidelineExpanded"
                  @update:checkpoint-expanded="setCheckpointExpanded"
                />
              </li>
            </ul>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
